import React, { useState } from 'react';
import {
    Box,
    Typography,
    Stack,
    IconButton,
    Link,
    Collapse,
    Chip,
    CircularProgress,
    useTheme, useMediaQuery,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CircleIcon from '@mui/icons-material/Circle';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ApprovalData } from '../types';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ApprovalDialog from './AddRemarkDialog';
import { ActionButton, CancelButton } from '../../../components/Button';
import { useNavigate } from 'react-router-dom';
import FilePreviewDialog from '../../../components/FilePreviewDialog/FilePreviewDialog';
import { useFilePreview } from '../../../hooks/useFilePreview';

import { indigentService } from '../../../services/indigentService';
import { useResponsiveToast } from '../../../components/Toast';
import { rolePermissions, getUserRolesFromToken } from '../../../config/routePermissions';

const statusColors = {
    Approved: {
        bg: '#D1FADF',
        color: '#027A48',
        dot: '#027A48',
    },
    Pending: {
        bg: '#FFF1C0',
        color: '#A3871E',
        dot: '#CFAC2C',
    },
    Rejected: {
        bg: '#FEE2E2',
        color: '#DC2626',
        dot: '#DC2626',
    },
    'Review Passed': {
        bg: '#DBEAFE',
        color: '#1D4ED8',
        dot: '#1D4ED8',
    },
    'Review Failed': {
        bg: '#FEF3C7',
        color: '#D97706',
        dot: '#D97706',
    },
};

type Props = {
    data: ApprovalData;
    onStatusUpdate?: (
        requestId: string,
        status: 'reviewed' | 'failed' | 'approved' | 'rejected',
        remark?: string,
        file?: File,
    ) => Promise<void>;
    isUpdating?: boolean;
};

const ApprovalCard: React.FC<Props> = ({ data, onStatusUpdate, isUpdating = false }) => {
    const [expanded, setExpanded] = useState(false);
    const [localIsUpdating, setLocalIsUpdating] = useState(false);
    const [showApprovalDialog, setShowApprovalDialog] = useState(false);
    const [dialogActionType, setDialogActionType] = useState<'approve' | 'reject' | 'pass' | 'fail'>('reject');
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isLargeTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
    const navigate = useNavigate();
    
    // File preview hook
    const { isOpen, filePath, filename, openPreview, closePreview } = useFilePreview();

    // Split attachments: supporting docs (scanned) vs review attachments (from Pass/Approve/etc.)
    const supportingAttachments = data.attachments?.filter(a => a.documentType !== 'review_attachment') ?? [];
    const reviewAttachments = data.attachments?.filter(a => a.documentType === 'review_attachment') ?? [];

    // Get current user roles directly from JWT token for security
    const userRoles = getUserRolesFromToken();

    // Check if user can perform actions using centralized function
    const canPerformActions = rolePermissions.canPerformActions(userRoles);

    const toggleExpand = () => setExpanded(!expanded);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };

    const { success: toastSuccess, error: toastError } = useResponsiveToast();

    const handleDuplicateToNextYear = async () => {
        setLocalIsUpdating(true);
        try {
            // Fetch current indigent details
            const current = await indigentService.getIndigentById(data.id);

            // Compute next financial year string
            const fyRaw = (current.financial_year || '').toString();
            let nextFinancialYear: string;
            const rangeMatch = fyRaw.match(/(\d{4})\s*\/\s*(\d{4})/);
            if (rangeMatch) {
                const start = parseInt(rangeMatch[1], 10);
                const end = parseInt(rangeMatch[2], 10);
                nextFinancialYear = `${start + 1}/${end + 1}`;
            } else {
                const yearMatch = fyRaw.match(/\d{4}/);
                const baseYear = yearMatch ? parseInt(yearMatch[0], 10) : new Date().getFullYear();
                nextFinancialYear = String(baseYear + 1);
            }

            // Transform to Create form's data shape (exclude images/signature/declaration)
            const prefillData = {
                // Step 1
                financialYear: nextFinancialYear,
                name: current.name || '',
                surname: current.surname || '',
                personalId: current.sa_id || '',
                employerName: current.employer_name || '',
                income: (current.monthly_income ?? '')?.toString(),
                incomeSource: current.source_of_income || '',
                cellNumber: current.cell_number || '',
                email: current.email || '',
                services: [
                    ...(current.service_electricity ? ['electricity'] : []),
                    ...(current.service_water ? ['water'] : []),
                    ...(current.service_waste ? ['waste'] : []),
                    ...(current.service_waste_water ? ['wastewater'] : []),
                ],

                // Step 2
                ward: current.ward_no || '',
                town: current.town || '',
                erfNo: current.erf_no || '',
                address1: current.address_line_1 || '',
                address2: current.address_line_2 || '',
                address3: current.address_line_3 || '',
                accountNumber: current.account_number || '',
                electricityMeterNo: current.electricity_meter_no || '',
                occupants:
                    (current.occupants || []).map(occ => ({
                        name: occ.name,
                        surname: occ.surname,
                        personalId: occ.personal_id,
                        employerName: occ.employer_name || '',
                        monthlyIncome: (occ.monthly_income ?? '')?.toString(),
                        cellNumber: occ.cell_number || '',
                    })) || [],
                occupantPhotos: [],

                // Step 3
                supportingDocs: {},
                document_types: [],
                files: [],
                deletedAttachmentIds: [],

                // Step 4
                legacyApplicationNumber: current.legacy_application_number || '',
                signatureImage: null,
                declarationAccepted: false,
            };

            // Navigate to create form with prefilled data
            navigate('/add', { state: { prefillIndigent: prefillData } });
            toastSuccess('Prefilled data for next financial year');
        } catch (err: any) {
            console.error('Error preparing duplicate indigent:', err);
            const message = err?.response?.data?.detail || err?.message || 'Failed to prepare duplicate';
            toastError(message);
        } finally {
            handleMenuClose();
            setLocalIsUpdating(false);
        }
    };

    const handleIndigentNumberClick = () => {
        // Navigate to edit page with the indigent ID as request ID
        navigate(`/edit/${data.id}`);
    };

    const handlePass = () => {
        setDialogActionType('pass');
        setShowApprovalDialog(true);
    };

    const handleFail = () => {
        setDialogActionType('fail');
        setShowApprovalDialog(true);
    };

    const handleApproveClick = () => {
        setDialogActionType('approve');
        setShowApprovalDialog(true);
    };

    const handleRejectClick = () => {
        setDialogActionType('reject');
        setShowApprovalDialog(true);
    };

    const handleDialogConfirm = async (remark: string, file?: File) => {
        if (!onStatusUpdate) return;

        setLocalIsUpdating(true);
        try {
            let status: 'reviewed' | 'failed' | 'approved' | 'rejected';
            
            switch (dialogActionType) {
                case 'pass':
                    status = 'reviewed';
                    break;
                case 'fail':
                    status = 'failed';
                    break;
                case 'approve':
                    status = 'approved';
                    break;
                case 'reject':
                    status = 'rejected';
                    break;
                default:
                    status = 'reviewed';
            }
            
            await onStatusUpdate(data.id, status, remark, file);
            setShowApprovalDialog(false);
        } catch (error) {
            console.error(`Error updating status to ${dialogActionType}:`, error);
        } finally {
            setLocalIsUpdating(false);
        }
    };

    const handleDialogCancel = () => {
        setShowApprovalDialog(false);
    };

    const isUpdatingStatus = isUpdating || localIsUpdating;

    return (
        <Box
            sx={{
                backgroundColor: '#FCFCFC',
                borderRadius: '16px',
                px: { xs: 2, sm: 2, md: 2, lg: 4 },
                py: { xs: 1.5, sm: 2, md: 2, lg: 3 },
                border: '1px solid #F2F0F0',
                fontFamily: 'Poppins, sans-serif',
                mb: { xs: 2, sm: 2.5, md: 3 },
            }}
        >
            {/* Header */}
            <Stack 
                direction={isMobile ? 'column' : 'row'}
                alignItems={isMobile ? 'stretch' : 'flex-start'}
                justifyContent='space-between'
                spacing={isMobile ? 2 : 0}
            >
                {/* Status + Indigent Number + Buttons for mobile */}
                {isMobile ? (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton
                                size="small"
                                onClick={handleMenuOpen}
                                disabled={isUpdatingStatus}
                                sx={{
                                    width: 20,
                                    height: 20,
                                    color: '#666',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    },
                                }}
                            >
                                <MoreVertIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                            <Chip
                                label={data.status}
                                icon={
                                    <CircleIcon
                                        sx={{ color: statusColors[data.status].dot, fontSize: 10 }}
                                    />
                                }
                                sx={{
                                    backgroundColor: statusColors[data.status].bg,
                                    color: statusColors[data.status].color,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    height: 28,
                                    borderRadius: '24px',
                                    px: 1,
                                    width: 'fit-content',
                                }}
                            />
                        </Stack>

                        {/* Action Buttons next to status for mobile - only for Pending status */}
                        {data.status === 'Pending' && canPerformActions && (
                            <Stack direction="row" spacing={1}>
                                <ActionButton
                                    onClick={handlePass}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        backgroundColor: '#132F5F',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: 11,
                                        height: 36,
                                        width: 65,
                                        minWidth: 65,
                                        '&:hover': { backgroundColor: '#0f254d' },
                                        '&:disabled': { backgroundColor: '#ccc' },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={18} color='inherit' />
                                    ) : (
                                        'Pass'
                                    )}
                                </ActionButton>
                                <CancelButton
                                    onClick={handleFail}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        minWidth: 65,
                                        p: 0,
                                        color: '#DE3B40',
                                        borderColor: '#DE3B40',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: 11,
                                        height: 36,
                                        width: 65,
                                        '&:hover': { backgroundColor: '#FFECEC' },
                                        '&:disabled': {
                                            color: '#ccc',
                                            borderColor: '#ccc',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={18} color='inherit' />
                                    ) : (
                                        'Fail'
                                    )}
                                </CancelButton>
                            </Stack>
                        )}
                    </Stack>
                ) : (
                    // Desktop/Tablet layout
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        {/* 3-Dot Menu Column */}
                        <IconButton
                            size="small"
                            onClick={handleMenuOpen}
                            disabled={isUpdatingStatus}
                            sx={{
                                width: 24,
                                height: 24,
                                color: '#666',
                                mt: 0.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>

                        {/* Status and Indigent Number Column */}
                        <Stack spacing={1} minWidth={isTablet ? 130 : 150}>
                            <Chip
                                label={data.status}
                                icon={
                                    <CircleIcon
                                        sx={{ color: statusColors[data.status].dot, fontSize: 10 }}
                                    />
                                }
                                sx={{
                                    backgroundColor: statusColors[data.status].bg,
                                    color: statusColors[data.status].color,
                                    fontWeight: 600,
                                    fontSize: { sm: 11, md: 12 },
                                    height: { sm: 28, md: 32 },
                                    borderRadius: '24px',
                                    px: 1,
                                    width: 'fit-content',
                                }}
                            />
                            <Typography fontSize={{ sm: 11, md: 12 }} fontWeight={400} color='#ABABAB'>
                                <b>Indigent Number:</b>
                            </Typography>
                            <Typography
                                component="span"
                                fontSize={{ sm: 11, md: 12 }}
                                sx={{
                                    textDecoration: 'underline',
                                    color: '#2979F1',
                                    cursor: 'pointer',
                                }}
                                onClick={handleIndigentNumberClick}
                            >
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                    <OpenInNewIcon sx={{ fontSize: { sm: 14, md: 16 } }} />
                                    {data.incidentNumber}
                                </Box>
                            </Typography>
                        </Stack>
                    </Stack>
                )}

                {/* Mobile Indigent Number Section */}
                {isMobile && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                        <Typography fontSize={11} fontWeight={400} color='#ABABAB'>
                            <b>Indigent Number:</b>
                        </Typography>
                        <Typography
                            component="span"
                            fontSize={11}
                            sx={{
                                textDecoration: 'underline',
                                color: '#2979F1',
                                cursor: 'pointer',
                            }}
                            onClick={handleIndigentNumberClick}
                        >
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <OpenInNewIcon sx={{ fontSize: 12 }} />
                                {data.incidentNumber}
                            </Box>
                        </Typography>
                    </Stack>
                )}

                {/* Main Content */}
                <Stack 
                    direction={isMobile ? 'column' : 'row'} 
                    spacing={isMobile ? 2 : (isTablet ? 3 : (isLargeTablet ? 5 : 8))} 
                    sx={{ 
                        mr: isMobile ? 0 : (isTablet ? 2 : (isLargeTablet ? 2 : 25)),
                        flex: 1,
                        minWidth: 0 // Allow content to shrink
                    }}
                >
                    {/* Left Column */}
                    <Stack spacing={isMobile ? 1.5 : (isTablet ? 1.5 : 2)} sx={{ minWidth: 0, flex: 1 }}>
                        <Stack spacing={0.5}>
                            <Typography
                                fontSize={{ xs: 11, sm: 12, md: 13 }}
                                fontWeight={400}
                                color='#49454F'
                                fontFamily='Poppins, sans-serif'
                            >
                                Full Name
                            </Typography>
                            <Typography
                                fontSize={{ xs: 13, sm: 14, md: 15 }}
                                fontWeight={600}
                                color='#1B1C1C'
                                fontFamily='Poppins, sans-serif'
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {data.fullName}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                            <Typography
                                fontSize={{ xs: 11, sm: 12, md: 13 }}
                                fontWeight={400}
                                color='#49454F'
                                fontFamily='Poppins, sans-serif'
                            >
                                Personal ID:
                            </Typography>
                            <Typography
                                fontSize={{ xs: 13, sm: 14, md: 15 }}
                                fontWeight={600}
                                color='#1B1C1C'
                                fontFamily='Poppins, sans-serif'
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {data.personalId}
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* Right Column */}
                    <Stack spacing={isMobile ? 1.5 : (isTablet ? 1.5 : 2)} sx={{ minWidth: 0, flex: 1 }}>
                        <Stack spacing={0.5}>
                            <Typography
                                fontSize={{ xs: 11, sm: 12, md: 13 }}
                                fontWeight={400}
                                color='#49454F'
                                fontFamily='Poppins, sans-serif'
                            >
                                {isMobile ? 'Meter No:' : 'Electricity Meter No:'}
                            </Typography>
                            <Typography
                                fontSize={{ xs: 13, sm: 14, md: 15 }}
                                fontWeight={600}
                                color='#1B1C1C'
                                fontFamily='Poppins, sans-serif'
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {data.electricityMeterNo}
                            </Typography>
                        </Stack>
                        <Stack spacing={0.5}>
                            <Typography
                                fontSize={{ xs: 11, sm: 12, md: 13 }}
                                fontWeight={400}
                                color='#49454F'
                                fontFamily='Poppins, sans-serif'
                            >
                                {isMobile ? 'Account No:' : 'Account Number:'}
                            </Typography>
                            <Typography
                                fontSize={{ xs: 13, sm: 14, md: 15 }}
                                fontWeight={600}
                                color='#1B1C1C'
                                fontFamily='Poppins, sans-serif'
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {data.accountNumber}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>

                {/* Buttons + Toggle */}
                {!isMobile && (
                    <Stack direction='row' spacing={isTablet ? 0.5 : 1} alignItems='center' sx={{ flexShrink: 0 }}>
                        {/* Show Pass/Fail buttons for pending status */}
                        {data.status === 'Pending' && canPerformActions && (
                            <>
                                <ActionButton
                                    onClick={handlePass}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        backgroundColor: '#132F5F',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: { sm: 12, md: 13, lg: 14 },
                                        height: { sm: 40, md: 44, lg: 48 },
                                        width: { sm: 65, md: 72, lg: 84 },
                                        minWidth: { sm: 65, md: 72, lg: 84 },
                                        '&:hover': { backgroundColor: '#0f254d' },
                                        '&:disabled': { backgroundColor: '#ccc' },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={isTablet ? 18 : 24} color='inherit' />
                                    ) : (
                                        'Pass'
                                    )}
                                </ActionButton>
                                <CancelButton
                                    onClick={handleFail}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        color: '#DE3B40',
                                        borderColor: '#DE3B40',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: { sm: 12, md: 13, lg: 14 },
                                        height: { sm: 40, md: 44, lg: 48 },
                                        width: { sm: 65, md: 72, lg: 84 },
                                        minWidth: { sm: 65, md: 72, lg: 84 },
                                        '&:hover': { backgroundColor: '#FFECEC' },
                                        '&:disabled': {
                                            color: '#ccc',
                                            borderColor: '#ccc',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={isTablet ? 18 : 24} color='inherit' />
                                    ) : (
                                        'Fail'
                                    )}
                                </CancelButton>
                            </>
                        )}

                        {/* Show Approve/Reject buttons for Review Passed status */}
                        {data.status === 'Review Passed' && canPerformActions && (
                            <>
                                <ActionButton
                                    onClick={handleApproveClick}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        backgroundColor: '#132F5F',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: { sm: 12, md: 13, lg: 14 },
                                        height: { sm: 40, md: 44, lg: 48 },
                                        width: { sm: 65, md: 72, lg: 84 },
                                        minWidth: { sm: 65, md: 72, lg: 84 },
                                        '&:hover': { backgroundColor: '#0f254d' },
                                        '&:disabled': { backgroundColor: '#ccc' },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={isTablet ? 18 : 24} color='inherit' />
                                    ) : (
                                        'Approve'
                                    )}
                                </ActionButton>
                                <CancelButton
                                    onClick={handleRejectClick}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        color: '#DE3B40',
                                        borderColor: '#DE3B40',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: { sm: 12, md: 13, lg: 14 },
                                        height: { sm: 40, md: 44, lg: 48 },
                                        width: { sm: 65, md: 72, lg: 84 },
                                        minWidth: { sm: 65, md: 72, lg: 84 },
                                        '&:hover': { backgroundColor: '#FFECEC' },
                                        '&:disabled': {
                                            color: '#ccc',
                                            borderColor: '#ccc',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={isTablet ? 18 : 24} color='inherit' />
                                    ) : (
                                        'Reject'
                                    )}
                                </CancelButton>
                            </>
                        )}

                        {/* Show Pass/Reject buttons for Review Failed status */}
                        {data.status === 'Review Failed' && canPerformActions && (
                            <>
                                <ActionButton
                                    onClick={handlePass}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        backgroundColor: '#132F5F',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: { sm: 12, md: 13, lg: 14 },
                                        height: { sm: 40, md: 44, lg: 48 },
                                        width: { sm: 65, md: 72, lg: 84 },
                                        minWidth: { sm: 65, md: 72, lg: 84 },
                                        '&:hover': { backgroundColor: '#0f254d' },
                                        '&:disabled': { backgroundColor: '#ccc' },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={isTablet ? 18 : 24} color='inherit' />
                                    ) : (
                                        'Pass'
                                    )}
                                </ActionButton>
                                <CancelButton
                                    onClick={handleRejectClick}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        color: '#DE3B40',
                                        borderColor: '#DE3B40',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: { sm: 12, md: 13, lg: 14 },
                                        height: { sm: 40, md: 44, lg: 48 },
                                        width: { sm: 65, md: 72, lg: 84 },
                                        minWidth: { sm: 65, md: 72, lg: 84 },
                                        '&:hover': { backgroundColor: '#FFECEC' },
                                        '&:disabled': {
                                            color: '#ccc',
                                            borderColor: '#ccc',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={isTablet ? 18 : 24} color='inherit' />
                                    ) : (
                                        'Reject'
                                    )}
                                </CancelButton>
                            </>
                        )}

                        <IconButton 
                            onClick={toggleExpand} 
                            disabled={isUpdatingStatus}
                            sx={{ 
                                width: { sm: 32, md: 36, lg: 40 }, 
                                height: { sm: 32, md: 36, lg: 40 } 
                            }}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                )}

                {/* Mobile Action Buttons for non-Pending statuses */}
                {isMobile && data.status !== 'Pending' && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {data.status === 'Review Passed' && canPerformActions && (
                            <>
                                <ActionButton
                                    onClick={handleApproveClick}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        backgroundColor: '#132F5F',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: 11,
                                        height: 36,
                                        width: 65,
                                        minWidth: 65,
                                        '&:hover': { backgroundColor: '#0f254d' },
                                        '&:disabled': { backgroundColor: '#ccc' },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={18} color='inherit' />
                                    ) : (
                                        'Approve'
                                    )}
                                </ActionButton>
                                <CancelButton
                                    onClick={handleRejectClick}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        color: '#DE3B40',
                                        borderColor: '#DE3B40',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: 11,
                                        height: 36,
                                        width: 65,
                                        minWidth: 65,
                                        '&:hover': { backgroundColor: '#FFECEC' },
                                        '&:disabled': {
                                            color: '#ccc',
                                            borderColor: '#ccc',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={18} color='inherit' />
                                    ) : (
                                        'Reject'
                                    )}
                                </CancelButton>
                            </>
                        )}

                        {data.status === 'Review Failed' && canPerformActions && (
                            <>
                                <ActionButton
                                    onClick={handlePass}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        backgroundColor: '#132F5F',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: 11,
                                        height: 36,
                                        width: 65,
                                        minWidth: 65,
                                        '&:hover': { backgroundColor: '#0f254d' },
                                        '&:disabled': { backgroundColor: '#ccc' },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={18} color='inherit' />
                                    ) : (
                                        'Pass'
                                    )}
                                </ActionButton>
                                <CancelButton
                                    onClick={handleRejectClick}
                                    disabled={isUpdatingStatus}
                                    sx={{
                                        color: '#DE3B40',
                                        borderColor: '#DE3B40',
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: 11,
                                        height: 36,
                                        width: 65,
                                        minWidth: 65,
                                        '&:hover': { backgroundColor: '#FFECEC' },
                                        '&:disabled': {
                                            color: '#ccc',
                                            borderColor: '#ccc',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    {isUpdatingStatus ? (
                                        <CircularProgress size={18} color='inherit' />
                                    ) : (
                                        'Reject'
                                    )}
                                </CancelButton>
                            </>
                        )}
                    </Stack>
                )}

                {/* Mobile Expansion Button - Only show for non-Pending statuses */}
                {isMobile && data.status !== 'Pending' && (
                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                        <IconButton onClick={toggleExpand} disabled={isUpdatingStatus} size="small">
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Stack>
                )}
            </Stack>

            {/* Expanded Details */}
            <Collapse in={expanded} unmountOnExit>
                <Box mt={2} /* sx={{ pl: isMobile ? 0 : (isTablet ? '100px' : (isLargeTablet ? '140px' : '160px')) }} */>
                    {/* Group fields into responsive columns */}
                    <Stack 
                        direction={isMobile ? 'column' : 'row'} 
                        spacing={isMobile ? 2 : (isTablet ? 4 : 6)} 
                        flexWrap='wrap'
                    >
                        {/* First Column */}
                        <Stack spacing={1} minWidth={isMobile ? '100%' : (isTablet ? 120 : 130)}>
                            <Detail label='Erf No' value={<b>{data.erfNo}</b>} />
                            <Detail label='Ward No' value={<b>{data.wardNo}</b>} />
                            <Detail label='Employer Name' value={<b>{data.employerName}</b>} />
                        </Stack>

                        {/* Second Column */}
                        <Stack spacing={1} minWidth={isMobile ? '100%' : (isTablet ? 200 : 220)}>
                            <Detail label='Monthly Income' value={<b>R{data.monthlyIncome}</b>} />
                            <Detail label='Financial Year' value={<b>{data.financialYear}</b>} />
                            <Detail label='Cell Number' value={<b>{data.cellNumber}</b>} />
                            {data.email && data.email !== 'N/A' && (
                                <Detail label='Email' value={<b>{data.email}</b>} />
                            )}
                        </Stack>
                    </Stack>

                    {/* Scanned Documents (supporting docs only; review_attachment shown below) */}
                    {supportingAttachments.length > 0 && (
                        <Box mt={3}>
                            <Typography fontSize={{ xs: 12, sm: 13, md: 14 }} fontWeight={600} mb={1}>
                                Scanned Documents:
                            </Typography>
                            <Stack direction='row' alignItems='center' spacing={0} flexWrap='wrap' sx={{ '& > *': { marginRight: '4px', marginBottom: '4px' } }}>
                                {supportingAttachments.map((attachment, index) => (
                                    <Box
                                        key={attachment.id}
                                        component='span'
                                        sx={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center' }}
                                    >
                                        <Link
                                            component="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                const nameWithExt = attachment.filePath?.split('/').pop() ?? attachment.filename ?? 'file';
                                                openPreview(attachment.filePath, nameWithExt);
                                            }}
                                            underline='hover'
                                            fontSize={{ xs: 12, sm: 13, md: 14 }}
                                            sx={{
                                                fontWeight: 500,
                                                color: '#132F5F',
                                                textDecoration: 'none',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    textDecoration: 'underline',
                                                }
                                            }}
                                        >
                                            {attachment.filename}
                                        </Link>
                                        {index < supportingAttachments.length - 1 && (
                                            <Typography component='span' fontSize={{ xs: 12, sm: 13, md: 14 }} color='text.secondary'>
                                                ,{' '}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Review attachments (optional files from Pass / Approve / Reject / Fail) */}
                    {reviewAttachments.length > 0 && (
                        <Box mt={3}>
                            <Typography fontSize={{ xs: 12, sm: 13, md: 14 }} fontWeight={600} mb={1}>
                                Review attachments:
                            </Typography>
                            <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='text.secondary' sx={{ mb: 1 }}>
                                Optional files added when changing status (Pass, Approve, etc.).
                            </Typography>
                            <Stack spacing={1}>
                                {reviewAttachments.map((attachment) => {
                                    const uploadedAt = attachment.uploadedAt
                                        ? (() => {
                                            try {
                                                return new Date(attachment.uploadedAt).toLocaleString(undefined, {
                                                    dateStyle: 'short',
                                                    timeStyle: 'short',
                                                });
                                            } catch {
                                                return attachment.uploadedAt;
                                            }
                                        })()
                                        : null;
                                    return (
                                        <Stack key={attachment.id} direction='row' alignItems='center' flexWrap='wrap' gap={0.5}>
                                            <Link
                                                component="button"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    const nameWithExt = attachment.filePath?.split('/').pop() ?? attachment.filename ?? 'file';
                                                    openPreview(attachment.filePath, nameWithExt);
                                                }}
                                                underline='hover'
                                                fontSize={{ xs: 12, sm: 13, md: 14 }}
                                                sx={{
                                                    fontWeight: 500,
                                                    color: '#132F5F',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    '&:hover': { textDecoration: 'underline' },
                                                }}
                                            >
                                                {attachment.filename}
                                            </Link>
                                            {uploadedAt && (
                                                <Typography component='span' fontSize={{ xs: 11, sm: 12 }} color='text.secondary'>
                                                    (uploaded {uploadedAt})
                                                </Typography>
                                            )}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Box>
                    )}

                    {/* Reviewer section */}
                    <Stack
                        direction={isMobile ? 'column' : 'row'}
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        justifyContent='space-between'
                        spacing={isMobile ? 1 : 0}
                        mt={3}
                    >
                        {(() => {
                            // Helper function to format date
                            const formatDate = (dateString: string | null | undefined) => {
                                if (!dateString) return '';
                                try {
                                    const date = new Date(dateString);
                                    return date.toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    });
                                } catch {
                                    return '';
                                }
                            };

                            // Show reviewer information based on status
                            switch (data.status) {
                                case 'Approved':
                                    const approvedBy = data.approved_by_username;
                                    const approvedAt = data.approved_at;
                                    return approvedBy ? (
                                        <Stack spacing={0.5}>
                                            <Typography fontSize={{ xs: 13, sm: 14, md: 15 }} fontWeight={500} color='#027A48'>
                                                Approved by {approvedBy}
                                            </Typography>
                                            {approvedAt && (
                                                <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='text.secondary'>
                                                    {formatDate(approvedAt)}
                                                </Typography>
                                            )}
                                            {data.reviewRemark && (
                                                <Box sx={{ 
                                                    mt: 1, 
                                                    p: 1.5, 
                                                    backgroundColor: '#D1FADF', 
                                                    borderRadius: '8px',
                                                    border: '1px solid #A7F3D0'
                                                }}>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} fontWeight={500} color='#065F46' mb={0.5}>
                                                        Review Remark:
                                                    </Typography>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='#065F46' sx={{ fontStyle: 'italic' }}>
                                                        "{data.reviewRemark}"
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    ) : null;
                                case 'Rejected':
                                    const rejectedBy = data.rejected_by_username;
                                    const rejectedAt = data.rejected_at;
                                    return rejectedBy ? (
                                        <Stack spacing={0.5}>
                                            <Typography fontSize={{ xs: 13, sm: 14, md: 15 }} fontWeight={500} color='#DC2626'>
                                                Rejected by {rejectedBy}
                                            </Typography>
                                            {rejectedAt && (
                                                <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='text.secondary'>
                                                    {formatDate(rejectedAt)}
                                                </Typography>
                                            )}
                                            {data.reviewRemark && (
                                                <Box sx={{ 
                                                    mt: 1, 
                                                    p: 1.5, 
                                                    backgroundColor: '#FEE2E2', 
                                                    borderRadius: '8px',
                                                    border: '1px solid #FECACA'
                                                }}>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} fontWeight={500} color='#991B1B' mb={0.5}>
                                                        Review Remark:
                                                    </Typography>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='#991B1B' sx={{ fontStyle: 'italic' }}>
                                                        "{data.reviewRemark}"
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    ) : null;
                                case 'Review Passed':
                                    const reviewPassedBy = data.review_passed_by_username;
                                    const reviewPassedAt = data.review_passed_at;
                                    return reviewPassedBy ? (
                                        <Stack spacing={0.5}>
                                            <Typography fontSize={{ xs: 13, sm: 14, md: 15 }} fontWeight={500} color='#1D4ED8'>
                                                Review Passed by {reviewPassedBy}
                                            </Typography>
                                            {reviewPassedAt && (
                                                <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='text.secondary'>
                                                    {formatDate(reviewPassedAt)}
                                                </Typography>
                                            )}
                                            {data.reviewRemark && (
                                                <Box sx={{ 
                                                    mt: 1, 
                                                    p: 1.5, 
                                                    backgroundColor: '#DBEAFE', 
                                                    borderRadius: '8px',
                                                    border: '1px solid #93C5FD'
                                                }}>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} fontWeight={500} color='#1E40AF' mb={0.5}>
                                                        Review Remark:
                                                    </Typography>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='#1E40AF' sx={{ fontStyle: 'italic' }}>
                                                        "{data.reviewRemark}"
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    ) : null;
                                case 'Review Failed':
                                    const reviewFailedBy = data.review_failed_by_username;
                                    const reviewFailedAt = data.review_failed_at;
                                    return reviewFailedBy ? (
                                        <Stack spacing={0.5}>
                                            <Typography fontSize={{ xs: 13, sm: 14, md: 15 }} fontWeight={500} color='#D97706'>
                                                Review Failed by {reviewFailedBy}
                                            </Typography>
                                            {reviewFailedAt && (
                                                <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='text.secondary'>
                                                    {formatDate(reviewFailedAt)}
                                                </Typography>
                                            )}
                                            {data.reviewRemark && (
                                                <Box sx={{ 
                                                    mt: 1, 
                                                    p: 1.5, 
                                                    backgroundColor: '#FEF3C7', 
                                                    borderRadius: '8px',
                                                    border: '1px solid #FDE68A'
                                                }}>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} fontWeight={500} color='#92400E' mb={0.5}>
                                                        Review Remark:
                                                    </Typography>
                                                    <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='#92400E' sx={{ fontStyle: 'italic' }}>
                                                        "{data.reviewRemark}"
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    ) : null;
                                default:
                                    return null;
                            }
                        })()}
                        {/* Avatar placeholder for future use */}
                        {/* <Avatar
                            src='/reviewer.jpg'
                            alt='Reviewer'
                            sx={{ 
                                width: { xs: 32, sm: 34, md: 36 }, 
                                height: { xs: 32, sm: 34, md: 36 }, 
                                border: '2px solid #E0E0E0' 
                            }}
                        /> */}
                    </Stack>
                </Box>
            </Collapse>

            {/* 3-Dot Menu */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                        minWidth: 220,
                    },
                }}
            >
                <MenuItem onClick={handleDuplicateToNextYear}>
                    <ListItemIcon>
                        <ContentCopyIcon fontSize="small" sx={{ color: '#666' }} />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Duplicate indigent to next financial year"
                        primaryTypographyProps={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: '#333',
                        }}
                    />
                </MenuItem>
            </Menu>

            {/* Reject Dialog */}
            <ApprovalDialog
                open={showApprovalDialog}
                onClose={handleDialogCancel}
                onConfirm={handleDialogConfirm}
                isLoading={isUpdatingStatus}
                actionType={dialogActionType}
            />

            {/* File Preview Dialog - Show on all devices */}
            <FilePreviewDialog
                open={isOpen}
                onClose={closePreview}
                filePath={filePath}
                filename={filename}
            />
        </Box>
    );
};

const Detail = ({ label, value }: { label: string; value: React.ReactNode }) => {
    // const theme = useTheme();
    // const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    // const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    return (
        <Stack spacing={0.2}>
            <Typography fontSize={{ xs: 11, sm: 12, md: 13 }} color='text.secondary'>
                {label}:
            </Typography>
            <Typography fontSize={{ xs: 12, sm: 13, md: 14 }}>{value}</Typography>
        </Stack>
    );
};

export default ApprovalCard;
